import type { StudentSummary } from '@school-erp/types';
import { PAYMENT_METHODS } from '@school-erp/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, apiBlob, apiList, saveBlob } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import {
  Notice,
  errorMessage,
  primaryButton,
  secondaryButton,
  selectClass,
} from '../../components/common';
import { TextField } from '../../components/TextField';
import { t, type MessageKey } from '../../i18n';
import { formatMinor, parseMajor } from '../../lib/money';

interface Statement {
  currency: string;
  balanceMinor: number;
  overdueMinor: number;
}
interface PaymentRow {
  id: string;
  receiptNumber: string;
  studentName: string;
  amountMinor: number;
  method: string;
  paidOn: string;
  status: 'valid' | 'void';
}

export function PaymentsTab({ schoolId }: { schoolId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [student, setStudent] = useState<StudentSummary | null>(null);
  const [form, setForm] = useState({ amount: '', method: 'cash', reference: '' });
  const [amountError, setAmountError] = useState<string | null>(null);
  const [voidReason, setVoidReason] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const matches = useQuery({
    queryKey: ['fees', schoolId, 'student-search', search],
    queryFn: () =>
      apiList<StudentSummary>('/students', { query: { schoolId, q: search, pageSize: 8 } }).then(
        (r) => r.data,
      ),
    enabled: search.trim().length >= 2 && can('payments.record', schoolId),
  });
  const statement = useQuery({
    queryKey: ['fees', schoolId, 'statement', student?.id],
    queryFn: () => api<Statement>(`/students/${student!.id}/fees`),
    enabled: !!student,
  });
  const payments = useQuery({
    queryKey: ['fees', schoolId, 'payments'],
    queryFn: async () => {
      const res = await apiList<PaymentRow>(`/schools/${schoolId}/payments`, {
        query: { pageSize: 20 },
      });
      return { rows: res.data, currency: (res as unknown as { currency: string }).currency };
    },
  });
  const currency = statement.data?.currency ?? payments.data?.currency ?? 'USD';

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['fees', schoolId] });
  const record = useMutation({
    mutationFn: (body: object) =>
      api<{ receiptNumber: string }>(`/schools/${schoolId}/payments`, { method: 'POST', body }),
    onSuccess: (receipt) => {
      setMessage({ kind: 'ok', text: t('fees.recorded', { number: receipt.receiptNumber }) });
      setForm({ amount: '', method: form.method, reference: '' });
      refresh();
    },
    onError: (e) => setMessage({ kind: 'error', text: errorMessage(e) }),
  });
  const voidIt = useMutation({
    mutationFn: (id: string) =>
      api(`/schools/${schoolId}/payments/${id}/void`, {
        method: 'POST',
        body: { reason: voidReason[id] },
      }),
    onSuccess: refresh,
    onError: (e) => setMessage({ kind: 'error', text: errorMessage(e) }),
  });

  const download = async (p: PaymentRow) => {
    try {
      saveBlob(
        await apiBlob(`/schools/${schoolId}/payments/${p.id}/receipt.pdf`),
        `receipt-${p.receiptNumber}.pdf`,
      );
    } catch (e) {
      setMessage({ kind: 'error', text: errorMessage(e) });
    }
  };

  return (
    <section aria-label={t('fees.tab.payments')} className="space-y-6">
      {message && <Notice kind={message.kind}>{message.text}</Notice>}

      {can('payments.record', schoolId) && (
        <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-4">
          <h2 className="text-lg font-semibold">{t('fees.collect')}</h2>
          <TextField
            label={t('fees.searchStudent')}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setStudent(null);
            }}
          />
          {!student && matches.data && matches.data.length > 0 && (
            <ul className="divide-y divide-slate-100 rounded border border-slate-200 text-sm">
              {matches.data.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    className="w-full p-2 text-left hover:bg-slate-50"
                    onClick={() => setStudent(s)}
                  >
                    {s.firstName} {s.lastName} · {s.admissionNumber}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {student && statement.data && (
            <>
              <p className="text-sm font-medium">
                {t('fees.owes', {
                  name: `${student.firstName} ${student.lastName}`,
                  amount: formatMinor(statement.data.balanceMinor, currency),
                })}
              </p>
              {statement.data.balanceMinor === 0 ? (
                <p className="text-slate-600">{t('fees.nothingDue')}</p>
              ) : (
                <form
                  noValidate
                  aria-label={t('fees.record')}
                  onSubmit={(e) => {
                    e.preventDefault();
                    const amountMinor = parseMajor(form.amount, currency);
                    if (amountMinor === null || amountMinor <= 0)
                      return setAmountError(t('fees.invalidAmount'));
                    setAmountError(null);
                    setMessage(null);
                    record.mutate({
                      studentId: student.id,
                      amountMinor,
                      method: form.method,
                      ...(form.reference.trim() ? { reference: form.reference.trim() } : {}),
                    });
                  }}
                  className="grid gap-4 sm:grid-cols-4"
                >
                  <TextField
                    label={`${t('fees.amount')} (${currency})`}
                    inputMode="decimal"
                    value={form.amount}
                    error={amountError}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  />
                  <div>
                    <label htmlFor="pay-method" className="block text-sm font-medium">
                      {t('fees.method')}
                    </label>
                    <select
                      id="pay-method"
                      value={form.method}
                      onChange={(e) => setForm({ ...form, method: e.target.value })}
                      className={selectClass}
                    >
                      {PAYMENT_METHODS.map((m) => (
                        <option key={m} value={m}>
                          {t(`fees.method.${m}` as MessageKey)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <TextField
                    label={t('fees.reference')}
                    value={form.reference}
                    maxLength={100}
                    onChange={(e) => setForm({ ...form, reference: e.target.value })}
                  />
                  <div className="self-end">
                    <button type="submit" className={primaryButton} disabled={record.isPending}>
                      {t('fees.record')}
                    </button>
                  </div>
                </form>
              )}
            </>
          )}
        </div>
      )}

      {payments.data?.rows.length === 0 && <p className="text-slate-600">{t('fees.noPayments')}</p>}
      {payments.data && payments.data.rows.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-600">
              <tr>
                <th scope="col" className="p-2">
                  {t('fees.receipt')}
                </th>
                <th scope="col" className="p-2">
                  {t('fees.student')}
                </th>
                <th scope="col" className="p-2">
                  {t('fees.date')}
                </th>
                <th scope="col" className="p-2">
                  {t('fees.method')}
                </th>
                <th scope="col" className="p-2 text-right">
                  {t('fees.amount')}
                </th>
                <th scope="col" className="p-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {payments.data.rows.map((p) => (
                <tr key={p.id} className={p.status === 'void' ? 'text-slate-400 line-through' : ''}>
                  <td className="p-2 font-mono text-xs">{p.receiptNumber}</td>
                  <th scope="row" className="p-2 font-medium">
                    {p.studentName}
                  </th>
                  <td className="p-2">{p.paidOn}</td>
                  <td className="p-2">{t(`fees.method.${p.method}` as MessageKey)}</td>
                  <td className="p-2 text-right">
                    {formatMinor(p.amountMinor, payments.data.currency)}
                  </td>
                  <td className="space-x-2 p-2 text-right">
                    <button
                      type="button"
                      className={secondaryButton}
                      aria-label={t('fees.receiptPdf', { number: p.receiptNumber })}
                      onClick={() => void download(p)}
                    >
                      PDF
                    </button>
                    {p.status === 'valid' && can('payments.void', schoolId) && (
                      <span className="inline-flex items-center gap-1">
                        <input
                          aria-label={`${t('fees.voidReason')}: ${p.receiptNumber}`}
                          placeholder={t('fees.voidReason')}
                          value={voidReason[p.id] ?? ''}
                          onChange={(e) => setVoidReason({ ...voidReason, [p.id]: e.target.value })}
                          className="w-36 rounded border border-slate-300 px-2 py-1 text-xs"
                        />
                        <button
                          type="button"
                          className="rounded border border-red-300 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                          disabled={!voidReason[p.id]?.trim() || voidIt.isPending}
                          aria-label={`${t('fees.void')}: ${p.receiptNumber}`}
                          onClick={() => voidIt.mutate(p.id)}
                        >
                          {t('fees.void')}
                        </button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
